<script setup lang="ts">
/**
 * OnlineIde.vue - Vue 3 wrapper for the Online IDE embedded variant
 *
 * This component wraps the MainEmbedded class from the Online IDE to work
 * within a Vue 3 application. It handles initialization, prop reactivity,
 * and proper cleanup on unmount.
 *
 * CRITICAL initialization order:
 * 1. Initialize Monaco workers globally (once per app lifetime)
 * 2. Load spritesheet globally (once per app lifetime)
 * 3. Create MainEmbedded instance with the container and file list
 */

import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import $ from 'jquery'
import * as PIXI from 'pixi.js'

// REGULAR imports (NOT import type) - we need the runtime values
import { MainEmbedded } from '@online-ide/client/embedded/MainEmbedded'
import { ThemeManager } from '@online-ide/client/main/gui/ThemeManager'
import type { JOScript } from '@online-ide/client/embedded/EmbeddedStarter'
import { SchedulerState } from '@online-ide/compiler/common/interpreter/SchedulerState.ts'
import type { GUIFile } from '@online-ide/client/workspace/File'
import type { PixiSpritesheetData } from '@online-ide/client/spritemanager/PixiSpritesheetData'

// Import IDE CSS
import '@online-ide/ide-styles'

// Monaco worker imports - MUST match EmbeddedStarter.ts exactly
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker'
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker'
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker'
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker'
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker'

// Spritesheet assets for graphics
import spritesheetjson from '@assets/ide/graphics/spritesheet.json.txt'
import spritesheetpng from '@assets/ide/graphics/spritesheet.png'

import FormularView from '../views/FormularView.vue'

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface IdeFile {
  /** File name including extension, e.g. "Main.java" */
  name: string
  /** File content/source code */
  content: string
  /** File type for icon and syntax highlighting (default: auto-detected from extension) */
  type?: 'java' | 'hint' | 'xml' | 'json' | 'text'
}

export interface IdeConfig {
  /** Animation speed: number of steps per second or 'max' for instant */
  speed: number | 'max'
  /** Show file list panel */
  withFileList: boolean
  /** Show console output panel */
  withConsole: boolean
  /** Show error list panel */
  withErrorList: boolean
  /** Show pseudo-code/disassembly panel */
  withPCode: boolean
  /** Show bottom panel with file list, console, etc. */
  withBottomPanel: boolean
  /** Hide the code editor (output/graphics only mode) */
  hideEditor: boolean
  /** Hide the start-up info panel */
  hideStartPanel: boolean
  /** Java libraries to include */
  libraries: string[]
  /** Enable file access API (window.online_ide_access) */
  enableFileAccess: boolean
}

type OnlineIdeProps = {
  files?: IdeFile[]
  /** Name of the file that should be shown/active in the editor */
  activeFile?: string
  config?: Partial<IdeConfig>
  theme?: 'light' | 'dark'
  runState?: 'stopped' | 'running' | 'paused'
  speedVal?: number
  isDebugging?: boolean
  fontSize?: number
  /** Deaktiviert den Feedback-Button (z.B. im öffentlichen Demo-Modus). */
  disableFeedback?: boolean
}

const props = withDefaults(defineProps<OnlineIdeProps>(), {
  files: () => [],
  activeFile: '',
  config: () => ({}),
  theme: 'dark',
  runState: 'stopped',
  speedVal: 100,
  fontSize: 14,
})

const emit = defineEmits<{
  /** IDE fully initialized and ready for interaction */
  ready: []
  /** Compilation finished, hasErrors indicates if there were errors */
  compile: [hasErrors: boolean]
  /** Compilation errors occurred */
  error: [errors: unknown[]]
  /** File content was changed by the user */
  change: [fileName: string, content: string]
  /** Feedback-Button wurde angeklickt, obwohl er (im Demo-Modus) deaktiviert ist */
  feedbackDisabled: []
  /** Interpreter state change of its own (e.g. program finished), keeps TopBar in sync */
  'update:runState': [state: 'stopped' | 'running' | 'paused']
}>()

// ============================================================================
// Refs
// ============================================================================

const ideContainer = ref<HTMLDivElement | null>(null)
const ideDiv = ref<HTMLDivElement | null>(null)

// Internal state
let mainEmbedded: MainEmbedded | null = null
let themeManager: ThemeManager | null = null
let autoSaveInterval: ReturnType<typeof setInterval> | null = null
let resizeObserver: ResizeObserver | null = null
let isInitialized = false
// Guards against a concurrent second initIde() while the first is still
// awaiting async setup (e.g. onMounted and the files watcher racing).
let isInitializing = false

// Feedback Modal state
const isFeedbackOpen = ref(false)
const editorCode = ref('')

// ============================================================================
// Monaco Worker Initialization (global, once per app)
// ============================================================================

let monacoWorkersInitialized = false

function initMonacoWorkers(): void {
  if (monacoWorkersInitialized) return

  // Exact code from EmbeddedStarter.ts
  self.MonacoEnvironment = {
    getWorker: (_workerId, label) => {
      switch (label) {
        case 'json':
          return new jsonWorker()
        case 'css':
        case 'scss':
        case 'less':
          return new cssWorker()
        case 'html':
        case 'handlebars':
        case 'razor':
          return new htmlWorker()
        case 'typescript':
        case 'javascript':
          return new tsWorker()
        default:
          return new editorWorker()
      }
    },
  }

  monacoWorkersInitialized = true
}

// ============================================================================
// Spritesheet Loading (global, once per app)
// ============================================================================

let spritesheetLoaded = false

async function loadSpritesheet(): Promise<void> {
  if (spritesheetLoaded) return

  try {
    // Exact code from EmbeddedStarter.ts
    const response = await fetch(spritesheetjson)
    const spritesheetData: PixiSpritesheetData = await response.json()

    const texture: PIXI.Texture = await PIXI.Assets.load(spritesheetpng)
    const source: PIXI.ImageSource = texture.source
    source.minFilter = 'nearest'
    source.magFilter = 'nearest'

    spritesheetData.meta.size.w = texture.width
    spritesheetData.meta.size.h = texture.height

    const spritesheet = new PIXI.Spritesheet(texture, spritesheetData)
    await spritesheet.parse()

    PIXI.Assets.cache.set('spritesheet', spritesheet)
    spritesheetLoaded = true
  } catch (error) {
    console.error('[OnlineIde] Failed to load spritesheet:', error)
  }
}

// ============================================================================
// IDE Initialization
// ============================================================================

function buildConfigJson(): string {
  const defaultConfig: IdeConfig = {
    speed: 'max',
    withFileList: true,
    withConsole: true,
    withErrorList: true,
    withPCode: true,
    withBottomPanel: true,
    hideEditor: false,
    hideStartPanel: false,
    libraries: [],
    enableFileAccess: false,
  }

  const mergedConfig = { ...defaultConfig, ...props.config }

  // The original code reads from data attribute and replaces double quotes with single quotes
  return JSON.stringify(mergedConfig).replace(/"/g, "'")
}

function buildScriptList(): JOScript[] {
  return props.files.map((file) => ({
    title: file.name,
    text: file.content,
    url: undefined,
  }))
}

async function initIde(): Promise<void> {
  console.log('[OnlineIde] initIde called')
  console.log('[OnlineIde] initIde - ideDiv.value:', ideDiv.value ? 'EXISTS' : 'NULL')
  console.log('[OnlineIde] initIde - props.files.length:', props.files.length)

  if (!ideDiv.value) {
    console.error('[OnlineIde] initIde FAILED: ideDiv is NULL - template ref not bound')
    return
  }
  if (props.files.length === 0) {
    console.warn('[OnlineIde] initIde SKIPPED: no files provided')
    return
  }
  if (isInitializing || isInitialized) {
    console.log('[OnlineIde] initIde SKIPPED: already initializing/initialized')
    return
  }

  isInitializing = true
  try {
    // Initialize global dependencies first
    initMonacoWorkers()
    console.log('[OnlineIde] Monaco workers initialized')

    await loadSpritesheet()
    console.log('[OnlineIde] Spritesheet loaded')

    // Ensure the div has proper dimensions for Monaco
    const divEl = ideDiv.value
    divEl.style.width = '100%'
    divEl.style.height = '100%'

    const $div = $(divEl)

    // Set the data attribute with config (single quotes become double quotes in readConfig)
    const configJson = buildConfigJson()
    $div.attr('data-java-online', configJson)

    // Build script list from files prop
    const scriptList = buildScriptList()
    console.log('[OnlineIde] Creating MainEmbedded with', scriptList.length, 'files')

    // Initialize ThemeManager BEFORE MainEmbedded to ensure Monaco theme is registered
    // Globaler data-mode liest den aktuellen App-Modus (Light/Dark)
    const globalMode = document.documentElement.getAttribute('data-mode')
    const theme: 'light' | 'dark' = globalMode === 'light' ? 'light' : 'dark'
    themeManager = new ThemeManager(ideDiv.value as HTMLDivElement)
    themeManager.switchTheme(theme)

    // Create the MainEmbedded instance
    mainEmbedded = new MainEmbedded($div, scriptList)
    console.log('[OnlineIde] MainEmbedded created successfully')

    // Set up event listeners for compile events
    if (mainEmbedded.getCompiler()) {
      mainEmbedded
        .getCompiler()
        .eventManager.on(
          'compilationFinishedWithNewExecutable',
          handleCompilationFinished,
          mainEmbedded
        )
    }

    // Report interpreter state changes (e.g. program finished) back to the TopBar
    mainEmbedded.getInterpreter()?.eventManager.on('stateChanged', handleInterpreterStateChanged)

    // Start auto-save interval (MainEmbedded creates one, but we track it)
    autoSaveInterval = setInterval(() => {
      // Auto-save is handled internally by MainEmbedded
    }, 1000)

    // Set up change detection for files
    setupChangeDetection()

    // Apply initial control states
    applyControlState()

    // Ensure the bottom panel visibility matches initial config
    const dom = ideDiv.value
    if (dom) {
      const bottomDiv = dom.querySelector('.joe_bottomPanelsWrapper') as HTMLElement
      if (bottomDiv) {
        bottomDiv.style.display = props.config?.withBottomPanel ? 'flex' : 'none'
      }
    }

    // Apply initial font size
    if (props.fontSize !== undefined) {
      if (mainEmbedded.editor && typeof mainEmbedded.editor.setFontSize === 'function') {
        mainEmbedded.editor.setFontSize(props.fontSize)
      } else {
        mainEmbedded.getMainEditor()?.updateOptions({ fontSize: props.fontSize })
      }
    }

    // Show the requested active file (otherwise MainEmbedded shows the first one)
    activateFile(props.activeFile)

    console.log('[OnlineIde] Initialization complete, emitting ready')
    isInitialized = true
    emit('ready')
  } catch (error) {
    console.error('[OnlineIde] Initialization failed:', error)
  } finally {
    isInitializing = false
  }
}

function toRunState(state: SchedulerState): 'stopped' | 'running' | 'paused' {
  if (state == SchedulerState.running) return 'running'
  if (state == SchedulerState.paused) return 'paused'
  return 'stopped'
}

function handleInterpreterStateChanged(oldState: SchedulerState, newState: SchedulerState): void {
  const runState = toRunState(newState)
  /** prevent that the state is changing when the old state is equal to the new state */
  if (toRunState(oldState) === runState) return
  if (runState !== props.runState) {
    emit('update:runState', runState)
  }
}

/**
 * Switch the editor to the file with the given name. The embedded IDE only
 * swaps the visible Monaco model when setFileActive is called, so this is what
 * makes selecting a file in the (Vue) sidebar actually update the editor.
 */
function activateFile(name?: string): void {
  if (!mainEmbedded || !name) return

  const workspace = mainEmbedded.getCurrentWorkspace()
  if (!workspace) return

  const file = workspace.getFiles().find((f) => f.name === name)
  if (file) {
    mainEmbedded.setFileActive(file)
  }
}

function handleCompilationFinished(): void {
  if (!mainEmbedded) return

  try {
    const currentWorkspace = mainEmbedded.getCurrentWorkspace()
    const errorManager = mainEmbedded.getBottomDiv()?.errorManager
    let hasErrors = false
    let errorsList: unknown[] = []

    if (errorManager) {
      const errors = errorManager.showErrors(currentWorkspace)
      hasErrors = errors && errors.size > 0

      if (hasErrors && errors) {
        errorsList = Array.from(errors.entries()).map(([file, errorList]) => ({
          file: (file as GUIFile).name,
          errors: errorList,
        }))
      }
    }

    emit('compile', hasErrors)

    if (hasErrors) {
      emit('error', errorsList)
    }
  } catch (e) {
    console.warn('[OnlineIde] Error handling compilation finished event:', e)
    emit('compile', false)
  }
}

function setupChangeDetection(): void {
  if (!mainEmbedded) return

  const workspace = mainEmbedded.getCurrentWorkspace()
  for (const file of workspace.getFiles()) {
    const model = file.getMonacoModel()
    if (model) {
      model.onDidChangeContent(() => {
        emit('change', file.name, file.getText())
      })
    }
  }
}

function applyControlState() {
  if (!mainEmbedded) return

  if (props.speedVal !== undefined) {
    const sc = mainEmbedded.programControlButtons?.speedControl
    if (sc) {
      sc.setSpeed((props.speedVal / 100) * (sc.xMax || 90))
    }
  }

  // Hide the default toolbar (buttons / controls) since we use our own TopBar
  const dom = ideDiv.value
  if (dom) {
    const hideBar = setInterval(() => {
      const oldBars = dom.querySelectorAll('.ide-toolbar, .toolbar, .joe_controlsDiv')
      if (oldBars && oldBars.length > 0) {
        oldBars.forEach((bar) => {
          ;(bar as HTMLElement).style.display = 'none'
        })
        clearInterval(hideBar)
      }
    }, 100)
    setTimeout(() => clearInterval(hideBar), 3000)
  }
}

// ============================================================================
// Resize Handling
// ============================================================================

function handleWindowResize(): void {
  if (mainEmbedded?.getMainEditor()) {
    mainEmbedded.getMainEditor().layout()
  }
}

function setupResizeObserver(): void {
  if (!ideContainer.value) return

  // Avoid leaking a previous observer if this runs more than once.
  resizeObserver?.disconnect()

  resizeObserver = new ResizeObserver(() => {
    if (mainEmbedded?.getMainEditor()) {
      mainEmbedded.getMainEditor().layout()
    }
  })

  resizeObserver.observe(ideContainer.value)

  // Also listen for window resize
  window.addEventListener('resize', handleWindowResize)
}

// ============================================================================
// Cleanup
// ============================================================================

function cleanup(): void {
  // Clear auto-save interval
  if (autoSaveInterval) {
    clearInterval(autoSaveInterval)
    autoSaveInterval = null
  }

  // Remove resize observer
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }

  // Remove window resize listener
  window.removeEventListener('resize', handleWindowResize)

  // Dispose Monaco editor
  if (mainEmbedded?.getMainEditor()) {
    mainEmbedded.getMainEditor().dispose()
  }

  // Clear only the IDE's own content. NOTE: do NOT empty `ideContainer` here -
  // that is the Vue-rendered root element and emptying it would detach the
  // Vue-managed `ideDiv` (and feedback button/modal) from the DOM, leaving the
  // template ref pointing at an orphaned node so the next init mounts into a
  // detached element and nothing renders.
  if (ideDiv.value) {
    $(ideDiv.value).empty()
  }

  mainEmbedded?.getInterpreter()?.eventManager.off(handleInterpreterStateChanged)

  mainEmbedded = null
  themeManager = null
  isInitialized = false
  isInitializing = false
}

// ============================================================================
// Theme Sync (global data-mode ↔ Editor-Theme)
// ============================================================================

function handleThemeModeChanged(event: Event): void {
  const mode = (event as CustomEvent).detail?.mode
  if (mode && themeManager) {
    themeManager.switchTheme(mode === 'light' ? 'light' : 'dark')
  }
}

// ============================================================================
// Lifecycle
// ============================================================================

onMounted(async () => {
  // Files are usually present from the first render (synchronous computed in
  // the parent). A non-immediate watcher does NOT fire on mount, so without
  // this hook the IDE would only initialize once the file set later *changes*
  // (e.g. switching folders) - which is why clicking files did nothing for the
  // first few seconds after a full page refresh.
  await initIde()
  setupResizeObserver()
  window.addEventListener('themeModeChanged', handleThemeModeChanged)
})

onUnmounted(() => {
  cleanup()
  window.removeEventListener('themeModeChanged', handleThemeModeChanged)
})

// ============================================================================
// Watchers
// ============================================================================

// Watch for file updates
watch(
  () => props.files.map((f) => f.name).join(','),
  async (newNames, oldNames) => {
    console.log('[OnlineIde] files watcher fired, signature:', newNames)
    console.log('[OnlineIde] files watcher - isInitialized:', isInitialized)

    if (isInitialized) {
      // Only re-init if files actually changed (folder switch)
      if (oldNames !== undefined && newNames !== oldNames) {
        console.log('[OnlineIde] File set changed, re-initializing IDE...')
        cleanup()
        await nextTick()
        await initIde()
        setupResizeObserver()
      } else {
        console.log('[OnlineIde] File set unchanged, skipping')
      }
    } else if (props.files.length > 0) {
      console.log('[OnlineIde] Files arrived after mount, initializing...')
      console.log('[OnlineIde] ideDiv.value:', ideDiv.value ? 'EXISTS' : 'NULL')
      await nextTick()
      console.log('[OnlineIde] after nextTick - ideDiv.value:', ideDiv.value ? 'EXISTS' : 'NULL')
      await initIde()
      setupResizeObserver()
    }
  }
)

// Watch for active file changes (e.g. clicking a file in the sidebar)
watch(
  () => props.activeFile,
  (name) => {
    if (isInitialized) {
      activateFile(name)
    }
  }
)

// Watch for config changes
watch(
  () => props.config,
  (newConfig, oldConfig) => {
    if (isInitialized) {
      if (newConfig && oldConfig && newConfig.withBottomPanel !== oldConfig.withBottomPanel) {
        const dom = ideDiv.value
        if (dom) {
          const bottomDiv = dom.querySelector('.joe_bottomPanelsWrapper') as HTMLElement
          if (bottomDiv) {
            bottomDiv.style.display = newConfig.withBottomPanel ? 'flex' : 'none'
            if (mainEmbedded?.getMainEditor()) {
              setTimeout(() => {
                mainEmbedded?.getMainEditor().layout()
              }, 50)
            }
          }
        }
      } else {
        console.warn('[OnlineIde] Config changes after initialization are not fully supported')
      }
    }
  },
  { deep: true }
)

// Watch for speed changes from TopBar
watch(
  () => props.isDebugging,
  (val) => {
    if (val) {
      mainEmbedded?.showDebugger()
    } else {
      mainEmbedded?.hideDebugger()
    }
  }
)
watch(
  () => props.speedVal,
  (val) => {
    if (mainEmbedded && val !== undefined) {
      const sc = mainEmbedded.programControlButtons?.speedControl
      if (sc) {
        sc.setSpeed((val / 100) * (sc.xMax || 90))
      }
    }
  }
)

// Watch for run/stop state changes from TopBar
watch(
  () => props.runState,
  (run) => {
    if (mainEmbedded && run !== undefined) {
      // Skip if the interpreter is already in this state (change came from the interpreter itself)
      const interpreterState = mainEmbedded.getInterpreter()?.scheduler.state
      if (interpreterState !== undefined && toRunState(interpreterState) === run) return

      if (run === 'running') {
        mainEmbedded.getActionManager().trigger('interpreter.start')
      } else if (run === 'paused') {
        mainEmbedded.getActionManager().trigger('interpreter.pause')
      } else if (run === 'stopped') {
        mainEmbedded.getActionManager().trigger('interpreter.stop')
      }
    }
  }
)

// Watch for theme changes
watch(
  () => props.theme,
  (newTheme) => {
    if (themeManager && newTheme) {
      themeManager.switchTheme(newTheme)
    }
  }
)

// Watch for font size zoom changes
watch(
  () => props.fontSize,
  (newSize) => {
    if (mainEmbedded && newSize !== undefined) {
      if (mainEmbedded.editor && typeof mainEmbedded.editor.setFontSize === 'function') {
        mainEmbedded.editor.setFontSize(newSize)
      } else {
        mainEmbedded.getMainEditor()?.updateOptions({ fontSize: newSize })
      }
    }
  }
)

// ============================================================================
// Feedback Modal Actions
// ============================================================================

const openFeedback = () => {
  if (mainEmbedded?.getMainEditor()) {
    editorCode.value = mainEmbedded.getMainEditor().getValue()
  }
  isFeedbackOpen.value = true
}

function triggerIdeAction(action: string, payload?: unknown) {
  if (!mainEmbedded) {
    console.warn('[IdeWrapper] Cannot trigger action, IDE not initialized')
    return
  }

  if (action.startsWith('keyboard.')) {
    const editorAction = action.split('.')[1] // 'undo' or 'redo'
    const editor = mainEmbedded.getMainEditor()
    if (editor) {
      editor.focus()
      editor.trigger('.', editorAction, {})
    }
  } else if (action === 'file.setContentUndoable') {
    const workspace = mainEmbedded.getCurrentWorkspace()
    const payloadData = payload as { name: string; content: string }
    const file = workspace
      .getFiles()
      .find((f: { name: string; getMonacoModel: () => unknown }) => f.name === payloadData.name)
    if (file && file.getMonacoModel()) {
      const model = file.getMonacoModel()
      if (model.getValue() !== payloadData.content) {
        model.pushEditOperations(
          [],
          [
            {
              range: model.getFullModelRange(),
              text: payloadData.content,
            },
          ],
          () => null
        )
      }
    }
  } else if (action === 'file.setContent') {
    const workspace = mainEmbedded.getCurrentWorkspace()
    const payloadData = payload as { name: string; content: string }
    const file = workspace
      .getFiles()
      .find((f: { name: string; getMonacoModel: () => unknown }) => f.name === payloadData.name)
    if (
      file &&
      (file as { getMonacoModel: () => { setValue: (v: string) => void } }).getMonacoModel()
    ) {
      ;(file as { getMonacoModel: () => { setValue: (v: string) => void } })
        .getMonacoModel()
        .setValue(payloadData.content)
    }
  } else {
    mainEmbedded.getActionManager().trigger(action)
  }
}

defineExpose({
  triggerIdeAction,
})
</script>

<template>
  <div ref="ideContainer" class="online-ide-wrapper bg-secondary" :class="[theme]">
    <div
      ref="ideDiv"
      class="java-online notranslate joeCssFence h-full w-full"
      :tabindex="0"
      style="display: flex; flex-direction: column"
    />

    <!-- Give Feedback Trigger Button -->
    <button
      class="absolute bottom-4 right-4 bg-accent text-text-main p-3 rounded-full shadow-lg transition-all z-10"
      :class="
        disableFeedback
          ? 'opacity-40 cursor-not-allowed'
          : 'hover:brightness-110 hover:scale-105 cursor-pointer'
      "
      :title="disableFeedback ? 'Im Demo-Modus nicht verfügbar' : 'Feedback abgeben'"
      @click="disableFeedback ? emit('feedbackDisabled') : openFeedback()"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path
          d="M12,3.5C6.5,3.5 2,6.5 2,10.14C2,12.19 3.5,14 6,15.14C5.5,17 4.14,18.5 2,19C4.5,19 7,17.5 8,16.5C9.3,16.8 10.6,17 12,17C17.5,17 22,14 22,10.14C22,6.5 17.5,3.5 12,3.5M12,5.5C16.4,5.5 20,7.6 20,10.14C20,12.7 16.4,14.8 12,14.8C10.8,14.8 9.6,14.6 8.5,14.25L7.75,14L7.14,14.5C6.4,15.1 5.38,15.7 4.25,16.14C4.85,15.14 5.3,14 5.4,12.85L5.5,12.14L4.85,11.75C3.7,11.14 3,10.25 3,10.14C3,7.6 6.5,5.5 12,5.5Z"
        />
      </svg>
    </button>

    <!-- Popup-Styled Feedback Modal -->
    <Transition name="fade">
      <div
        v-if="isFeedbackOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        @click.self="closeFeedback"
      >
        <div
          class="w-full max-w-md bg-secondary border border-tertiary p-8 rounded-outer-popup shadow-2xl text-left transform transition-all duration-300 scale-100 relative"
        >
          <!-- Close Button -->
          <button
            class="absolute top-5 right-5 text-text-main/60 hover:text-text-main text-2xl font-bold cursor-pointer"
            aria-label="Schließen"
            @click="closeFeedback"
          >
            &times;
          </button>

          <h3
            class="text-xl font-bold text-text-main flex items-center gap-2 border-b border-tertiary pb-3 mb-5"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="text-accent">
              <path
                d="M12,3.5C6.5,3.5 2,6.5 2,10.14C2,12.19 3.5,14 6,15.14C5.5,17 4.14,18.5 2,19C4.5,19 7,17.5 8,16.5C9.3,16.8 10.6,17 12,17C17.5,17 22,14 22,10.14C22,6.5 17.5,3.5 12,3.5M12,5.5C16.4,5.5 20,7.6 20,10.14C20,12.7 16.4,14.8 12,14.8C10.8,14.8 9.6,14.6 8.5,14.25L7.75,14L7.14,14.5C6.4,15.1 5.38,15.7 4.25,16.14C4.85,15.14 5.3,14 5.4,12.85L5.5,12.14L4.85,11.75C3.7,11.14 3,10.25 3,10.14C3,7.6 6.5,5.5 12,5.5Z"
              />
            </svg>
            Feedback abgeben
          </h3>

          <form class="space-y-4" @submit.prevent="submitFeedback">
            <div>
              <label class="block text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1.5">
                Art des Feedbacks
              </label>
              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="type in ['Bug', 'Feature', 'Sonstiges']"
                  :key="type"
                  type="button"
                  class="py-2 text-xs font-semibold rounded-standard border transition-all duration-150 cursor-pointer"
                  :class="
                    feedbackType === type
                      ? 'bg-accent border-accent text-white shadow-sm'
                      : 'bg-primary border-tertiary text-text-main/70 hover:bg-primary/80'
                  "
                  @click="feedbackType = type"
                >
                  {{ type }}
                </button>
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1.5">
                Deine E-Mail-Adresse
              </label>
              <input
                v-model="feedbackEmail"
                type="email"
                required
                placeholder="name@beispiel.de"
                class="w-full px-4 py-2 bg-primary border border-tertiary rounded-inner-popup text-sm text-text-main placeholder:text-text-main/30 focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>

            <div>
              <label class="block text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1.5">
                Beschreibung
              </label>
              <textarea
                v-model="feedbackText"
                rows="4"
                required
                placeholder="Schreibe uns deine Anmerkungen oder beschreibe den Fehler..."
                class="w-full px-4 py-2 bg-primary border border-tertiary rounded-inner-popup text-sm text-text-main placeholder:text-text-main/30 focus:outline-none focus:ring-1 focus:ring-accent resize-none"
              />
            </div>

            <div class="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                class="px-4 py-2 bg-primary border border-tertiary text-text-main text-xs font-semibold rounded-standard cursor-pointer transition-colors duration-150 hover:bg-primary/80"
                @click="closeFeedback"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                class="px-5 py-2 bg-accent text-white text-xs font-semibold rounded-standard cursor-pointer transition-all duration-150 hover:brightness-110 shadow-sm"
              >
                Absenden
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
    <FormularView v-if="isFeedbackOpen" v-model="isFeedbackOpen" :editor-code="editorCode" />
  </div>
</template>

<style scoped>
.online-ide-wrapper {
  width: 100%;
  height: 100%;
  min-height: 400px;
  overflow: hidden;
  border-radius: var(--radius-standard, 8px);
  border: 1px solid var(--border, #ddd);
  position: relative;
}

/* Ensure nested elements don't leak styles */
.online-ide-wrapper :deep(*) {
  box-sizing: border-box;
}

/* Entferne den unerwünschten margin von .joeCssFence (embedded.css),
   der einen sichtbaren Balken zwischen Wrapper und Editor erzeugt. */
.online-ide-wrapper :deep(.java-online.joeCssFence) {
  margin: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
